import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../../styles/zLayers';
import ColorPickerPopover from '../ColorPickerPopover';
import { PopoverGroup, useExclusivePopover } from './PopoverGroup';

export interface ElementDesignValue {
  bgEnabled: boolean;
  bgColor: string;
  bgOpacity: number;       // 0-100
  borderEnabled: boolean;
  borderColor: string;
  borderThickness: number; // px at reference width 360
  borderRadius: number;    // px at reference width 360
}

interface ElementDesignPopoverProps {
  value: ElementDesignValue;
  onChange: (next: ElementDesignValue) => void;
  presetColors?: string[];
  position?: 'above' | 'below' | 'auto';
  disabled?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

const POPOVER_W = 240;
const POPOVER_H = 220;

type PopoverCoords = { top?: number; bottom?: number; left: number };

const ElementDesignPopover: React.FC<ElementDesignPopoverProps> = ({
  value,
  onChange,
  presetColors,
  position = 'above',
  disabled = false,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useExclusivePopover();
  const [coords, setCoords] = useState<PopoverCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const innerPickerOpenRef = useRef(false);

  useEffect(() => { onOpenChange?.(isOpen); }, [isOpen, onOpenChange]);

  const calc = useCallback((): PopoverCoords | null => {
    if (!triggerRef.current) return null;
    const r = triggerRef.current.getBoundingClientRect();
    // Anchor to the toolbar pill (parent), not just the trigger button — that
    // way the popover sits flush against the visual edge of the toolbar with
    // zero gap, even with the pill's vertical padding.
    const parent = triggerRef.current.parentElement;
    const anchor = parent ? parent.getBoundingClientRect() : r;
    const goAbove = position === 'above' || (position === 'auto' && anchor.top > POPOVER_H + 10);
    // For "above": anchor by the popover's BOTTOM so it sits flush against
    // the toolbar's top edge regardless of the popover's actual content
    // height (which varies as bg/border sections expand). For "below":
    // anchor by the top.
    // Left-align with the trigger button so the popover visually connects to
    // the toolbar control it expands from.
    if (goAbove) {
      return {
        bottom: window.innerHeight - anchor.top,
        left: r.left,
      };
    }
    return {
      top: anchor.bottom,
      left: r.left,
    };
  }, [position]);

  useEffect(() => {
    if (!isOpen) return;
    setCoords(calc());
    const onScroll = () => setCoords(calc());
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [isOpen, calc]);

  useEffect(() => {
    if (!isOpen) return;
    const onClick = (e: MouseEvent) => {
      if (innerPickerOpenRef.current) return;
      const t = e.target as Node;
      if (popoverRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setIsOpen(false);
    };
    // Defer listener install so the same click that opened us doesn't close us.
    const tid = setTimeout(() => document.addEventListener('mousedown', onClick), 10);
    return () => {
      clearTimeout(tid);
      document.removeEventListener('mousedown', onClick);
    };
  }, [isOpen, setIsOpen]);

  const set = (patch: Partial<ElementDesignValue>) => onChange({ ...value, ...patch });

  const anyActive = value.bgEnabled || value.borderEnabled;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setIsOpen(!isOpen); }}
        disabled={disabled}
        title="Element design"
        aria-label="Element design (background and border)"
        className={`w-6 h-6 flex items-center justify-center rounded border transition-colors ${anyActive
          ? 'bg-blue-500 border-blue-400 text-white'
          : 'bg-black/40 border-white/20 text-white/80 hover:bg-black/60'}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <rect x="4" y="6" width="16" height="12" rx="2" />
          <line x1="4" y1="12" x2="20" y2="12" strokeDasharray="2 2" />
        </svg>
      </button>
      {isOpen && coords && createPortal(
        // Nested group: the swatches inside are exclusive among themselves
        // without closing this panel around them.
        <PopoverGroup>
        <div
          ref={popoverRef}
          data-overlay-popover=""
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: coords.top,
            bottom: coords.bottom,
            left: coords.left,
            width: POPOVER_W,
            zIndex: Z.PICKER,
          }}
          className="bg-zinc-900/95 backdrop-blur rounded-lg border border-white/15 shadow-xl p-3 flex flex-col gap-2.5"
        >
          {/* Background section */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wide text-white/60">Background</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); set({ bgEnabled: !value.bgEnabled }); }}
                className={`text-[10px] px-2 py-0.5 rounded border ${value.bgEnabled ? 'bg-blue-500 border-blue-400 text-white' : 'bg-black/40 border-white/20 text-white/70'}`}
              >
                {value.bgEnabled ? 'On' : 'Off'}
              </button>
            </div>
            {value.bgEnabled && (
              <div className="flex items-center gap-2">
                <ColorPickerPopover
                  color={value.bgColor}
                  onChange={(c) => set({ bgColor: c })}
                  presetColors={presetColors}
                  position="below"
                  triggerSize="md"
                  onOpenChange={(o) => { innerPickerOpenRef.current = o; }}
                />
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={value.bgOpacity}
                  onChange={(e) => set({ bgOpacity: parseInt(e.target.value, 10) })}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 accent-blue-500"
                  title="Background opacity"
                />
                <span className="text-[10px] text-white/70 w-7 text-right">{value.bgOpacity}%</span>
              </div>
            )}
          </div>

          <div className="h-px bg-white/10" />

          {/* Border section */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wide text-white/60">Border</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); set({ borderEnabled: !value.borderEnabled }); }}
                className={`text-[10px] px-2 py-0.5 rounded border ${value.borderEnabled ? 'bg-blue-500 border-blue-400 text-white' : 'bg-black/40 border-white/20 text-white/70'}`}
              >
                {value.borderEnabled ? 'On' : 'Off'}
              </button>
            </div>
            {value.borderEnabled && (
              <>
                <div className="grid grid-cols-[3.25rem_1fr_1.75rem] items-center gap-2">
                  <span className="text-[8px] uppercase tracking-wide text-white/50">Color</span>
                  <ColorPickerPopover
                    color={value.borderColor}
                    onChange={(c) => set({ borderColor: c })}
                    presetColors={presetColors}
                    position="below"
                    triggerSize="md"
                    onOpenChange={(o) => { innerPickerOpenRef.current = o; }}
                  />
                  <span />
                </div>
                <div className="grid grid-cols-[3.25rem_1fr_1.75rem] items-center gap-2">
                  <span className="text-[8px] uppercase tracking-wide text-white/50">Thickness</span>
                  <input
                    type="range"
                    min={0}
                    max={10}
                    value={value.borderThickness}
                    onChange={(e) => set({ borderThickness: parseInt(e.target.value, 10) })}
                    onClick={(e) => e.stopPropagation()}
                    className="accent-blue-500 w-full min-w-0"
                    title="Border thickness"
                  />
                  <span className="text-[10px] text-white/70 text-right whitespace-nowrap">{value.borderThickness}px</span>
                </div>
                <div className="grid grid-cols-[3.25rem_1fr_1.75rem] items-center gap-2">
                  <span className="text-[8px] uppercase tracking-wide text-white/50">Radius</span>
                  <input
                    type="range"
                    min={0}
                    max={32}
                    value={value.borderRadius}
                    onChange={(e) => set({ borderRadius: parseInt(e.target.value, 10) })}
                    onClick={(e) => e.stopPropagation()}
                    className="accent-blue-500 w-full min-w-0"
                    title="Corner radius"
                  />
                  <span className="text-[10px] text-white/70 text-right whitespace-nowrap">{value.borderRadius}px</span>
                </div>
              </>
            )}
          </div>
        </div>
        </PopoverGroup>,
        document.body,
      )}
    </>
  );
};

export default ElementDesignPopover;
